import { BaseRepository, QueryOptions, RepositoryError } from './BaseRepository';

/**
 * Agreement entity interface
 * Represents a unified agreement/membership across all systems
 */
export interface Agreement {
  id: string;
  agreement_number: string;
  agreement_type: 'membership' | 'warranty' | 'service_contract';
  customer_id: string;
  plan_id: string | null;
  product_id: string | null;
  status: 'active' | 'suspended' | 'cancelled' | 'expired';
  start_date: string;
  end_date: string | null;
  renewal_date: string | null;
  cancelled_date: string | null;
  premium_amount: number;
  billing_frequency: 'monthly' | 'quarterly' | 'annual' | null;
  payment_method: string | null;
  term_months: number | null;
  auto_renew: boolean;
  coverage_details: Record<string, any> | null;
  equipment_ids: string[] | null;
  sw_agreement_id: string | null;
  warranty_agreement_id: string | null;
  membership_id: string | null;
  source_system: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

/**
 * Filter options for agreement queries
 */
export interface AgreementFilters {
  search?: string;
  customerId?: string;
  agreementType?: 'membership' | 'warranty' | 'service_contract';
  status?: 'active' | 'suspended' | 'cancelled' | 'expired';
  startDateFrom?: string;
  startDateTo?: string;
  expiringWithinDays?: number;
  autoRenew?: boolean;
  minPremium?: number;
  maxPremium?: number;
}

/**
 * Agreement statistics
 */
export interface AgreementStatistics {
  total: number;
  active: number;
  expired: number;
  cancelled: number;
  suspended: number;
  totalPremiumValue: number;
  averagePremium: number;
  expiringNextMonth: number;
  autoRenewCount: number;
}

/**
 * Agreement Repository
 * 
 * Manages all agreement/membership data access operations.
 * Consolidates memberships, warranty_agreements, and sw_agreements.
 * 
 * @example
 * ```typescript
 * const agreementRepo = new AgreementRepository();
 * 
 * // Find active agreements
 * const active = await agreementRepo.findByStatus('active');
 * 
 * // Find agreements expiring soon
 * const expiring = await agreementRepo.findExpiringSoon(30);
 * 
 * // Get statistics
 * const stats = await agreementRepo.getStatistics();
 * ```
 */
export class AgreementRepository extends BaseRepository<Agreement> {
  constructor() {
    super({
      tableName: 'unified_agreements', // Will be consolidated in Phase 2
      primaryKey: 'id'
    });
  }

  /**
   * Find agreements by various filters
   * 
   * @param filters Agreement-specific filter options
   * @param options Query options (pagination, ordering)
   * @returns Promise resolving to array of matching agreements
   */
  async findByFilters(
    filters: AgreementFilters,
    options?: QueryOptions
  ): Promise<Agreement[]> {
    let query = this.client.from(this.config.tableName).select('*');

    // Search across multiple fields
    if (filters.search) {
      query = query.or(`
        agreement_number.ilike.%${filters.search}%,
        customer_id.ilike.%${filters.search}%
      `);
    }

    // Customer filter
    if (filters.customerId) {
      query = query.eq('customer_id', filters.customerId);
    }

    // Agreement type filter
    if (filters.agreementType) {
      query = query.eq('agreement_type', filters.agreementType);
    }

    // Status filter
    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    // Date range filters
    if (filters.startDateFrom) {
      query = query.gte('start_date', filters.startDateFrom);
    }

    if (filters.startDateTo) {
      query = query.lte('start_date', filters.startDateTo);
    }

    // Expiring soon filter
    if (filters.expiringWithinDays) {
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + filters.expiringWithinDays);
      query = query
        .lte('end_date', futureDate.toISOString())
        .eq('status', 'active');
    }

    // Auto renew filter
    if (filters.autoRenew !== undefined) {
      query = query.eq('auto_renew', filters.autoRenew);
    }

    // Premium range filters
    if (filters.minPremium !== undefined) {
      query = query.gte('premium_amount', filters.minPremium);
    }

    if (filters.maxPremium !== undefined) {
      query = query.lte('premium_amount', filters.maxPremium);
    }

    // Apply ordering
    if (options?.orderBy) {
      query = query.order(options.orderBy.column, { 
        ascending: options.orderBy.ascending 
      });
    } else {
      // Default: order by start date descending
      query = query.order('start_date', { ascending: false });
    }

    // Apply pagination
    if (options?.limit) {
      const from = options.offset || 0;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error } = await query;

    if (error) {
      throw new RepositoryError(
        `Failed to fetch agreements with filters`, 
        error
      );
    }

    return (data || []) as Agreement[];
  }

  /**
   * Get agreement statistics
   * 
   * @returns Promise resolving to agreement statistics
   */
  async getStatistics(): Promise<AgreementStatistics> {
    const { data, error } = await this.client
      .from(this.config.tableName)
      .select('status, premium_amount, auto_renew, end_date');

    if (error) {
      throw new RepositoryError(
        `Failed to fetch agreement statistics`, 
        error
      );
    }

    const agreements = data || [];
    const now = new Date();
    const nextMonth = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    return {
      total: agreements.length,
      active: agreements.filter(a => a.status === 'active').length,
      expired: agreements.filter(a => a.status === 'expired').length,
      cancelled: agreements.filter(a => a.status === 'cancelled').length,
      suspended: agreements.filter(a => a.status === 'suspended').length,
      totalPremiumValue: agreements.reduce((sum, a) => sum + (a.premium_amount || 0), 0),
      averagePremium: agreements.length > 0 
        ? agreements.reduce((sum, a) => sum + (a.premium_amount || 0), 0) / agreements.length 
        : 0,
      expiringNextMonth: agreements.filter(a => 
        a.status === 'active' && 
        a.end_date && 
        new Date(a.end_date) <= nextMonth
      ).length,
      autoRenewCount: agreements.filter(a => a.auto_renew).length
    };
  }

  /**
   * Find agreements by customer ID
   * 
   * @param customerId Customer ID to filter by
   * @param options Query options
   * @returns Promise resolving to array of agreements
   */
  async findByCustomer(
    customerId: string,
    options?: QueryOptions
  ): Promise<Agreement[]> {
    let query = this.client
      .from(this.config.tableName)
      .select('*')
      .eq('customer_id', customerId);

    // Apply ordering
    if (options?.orderBy) {
      query = query.order(options.orderBy.column, { 
        ascending: options.orderBy.ascending 
      });
    } else {
      query = query.order('start_date', { ascending: false });
    }

    const { data, error } = await query;

    if (error) {
      throw new RepositoryError(
        `Failed to find agreements by customer`, 
        error
      );
    }

    return (data || []) as Agreement[];
  }

  /**
   * Find agreements by status
   * 
   * @param status Agreement status to filter by
   * @param options Query options
   * @returns Promise resolving to array of agreements
   */
  async findByStatus(
    status: 'active' | 'suspended' | 'cancelled' | 'expired',
    options?: QueryOptions
  ): Promise<Agreement[]> {
    let query = this.client
      .from(this.config.tableName)
      .select('*')
      .eq('status', status);

    // Apply ordering
    if (options?.orderBy) {
      query = query.order(options.orderBy.column, { 
        ascending: options.orderBy.ascending 
      });
    }

    // Apply pagination
    if (options?.limit) {
      const from = options.offset || 0;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error } = await query;

    if (error) {
      throw new RepositoryError(
        `Failed to find agreements by status`, 
        error
      );
    }

    return (data || []) as Agreement[];
  }

  /**
   * Find agreements expiring within specified days
   * 
   * @param days Number of days to look ahead
   * @param options Query options
   * @returns Promise resolving to array of agreements
   */
  async findExpiringSoon(
    days: number = 30,
    options?: QueryOptions
  ): Promise<Agreement[]> {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + days);

    let query = this.client
      .from(this.config.tableName)
      .select('*')
      .eq('status', 'active')
      .not('end_date', 'is', null)
      .lte('end_date', futureDate.toISOString());

    // Apply ordering - closest expiration first
    query = query.order('end_date', { ascending: true });

    // Apply pagination
    if (options?.limit) {
      const from = options.offset || 0;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error } = await query;

    if (error) {
      throw new RepositoryError(
        `Failed to find expiring agreements`, 
        error
      );
    }

    return (data || []) as Agreement[];
  }

  /**
   * Find agreements by type
   * 
   * @param type Agreement type
   * @param options Query options
   * @returns Promise resolving to array of agreements
   */
  async findByType(
    type: 'membership' | 'warranty' | 'service_contract',
    options?: QueryOptions
  ): Promise<Agreement[]> {
    return this.findByFilters({ agreementType: type }, options);
  }

  /**
   * Find agreement by agreement number
   * 
   * @param agreementNumber Agreement number to search for
   * @returns Promise resolving to agreement or null
   */
  async findByAgreementNumber(agreementNumber: string): Promise<Agreement | null> {
    const { data, error } = await this.client
      .from(this.config.tableName)
      .select('*')
      .eq('agreement_number', agreementNumber)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new RepositoryError(
        `Failed to find agreement by number`, 
        error
      );
    }

    return data as Agreement;
  }

  /**
   * Find agreements with auto-renew enabled
   * 
   * @param options Query options
   * @returns Promise resolving to array of agreements
   */
  async findAutoRenew(options?: QueryOptions): Promise<Agreement[]> {
    let query = this.client
      .from(this.config.tableName)
      .select('*')
      .eq('auto_renew', true)
      .eq('status', 'active');

    // Apply ordering
    if (options?.orderBy) {
      query = query.order(options.orderBy.column, { 
        ascending: options.orderBy.ascending 
      });
    } else {
      query = query.order('renewal_date', { ascending: true });
    }

    // Apply pagination
    if (options?.limit) {
      const from = options.offset || 0;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error } = await query;

    if (error) {
      throw new RepositoryError(
        `Failed to find auto-renew agreements`, 
        error
      );
    }

    return (data || []) as Agreement[];
  }

  /**
   * Cancel an agreement
   * 
   * @param id Agreement ID
   * @param cancelledBy User who cancelled the agreement
   * @returns Promise resolving to updated agreement
   */
  async cancel(id: string, cancelledBy: string): Promise<Agreement> {
    return this.update(id, {
      status: 'cancelled',
      cancelled_date: new Date().toISOString(),
      updated_by: cancelledBy
    } as Partial<Agreement>);
  }

  /**
   * Suspend an agreement
   * 
   * @param id Agreement ID
   * @param suspendedBy User who suspended the agreement
   * @returns Promise resolving to updated agreement
   */
  async suspend(id: string, suspendedBy: string): Promise<Agreement> {
    return this.update(id, {
      status: 'suspended',
      updated_by: suspendedBy
    } as Partial<Agreement>);
  }

  /**
   * Reactivate a suspended agreement
   * 
   * @param id Agreement ID
   * @param reactivatedBy User who reactivated the agreement
   * @returns Promise resolving to updated agreement
   */
  async reactivate(id: string, reactivatedBy: string): Promise<Agreement> {
    return this.update(id, {
      status: 'active',
      updated_by: reactivatedBy
    } as Partial<Agreement>);
  }
}
